import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { collectClientHints, requestGeolocation } from "@/lib/clientHints";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  FileHeart, FlaskConical, Database, Terminal, Archive, ShieldCheck,
  Cpu, Wifi, Globe2, HardDrive, ShieldAlert, RefreshCw, Lock,
} from "lucide-react";
import { cn } from "@/lib/utils";

const ICONS = {
  medical_record: FileHeart,
  clinical_trial: FlaskConical,
  dataset: Database,
  internal_app: Terminal,
  storage: Archive,
};

export default function CatalogPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSimulated = user?.mode === "simulated";

  const [resources, setResources] = useState([]);
  const [posture, setPosture] = useState(null);
  const [postureLoading, setPostureLoading] = useState(true);
  const [geoPermission, setGeoPermission] = useState(null); // null / true / false

  // Simulated-mode overrides (only used when mode === simulated)
  const [simPosture, setSimPosture] = useState({
    os: "macOS", os_updated: true, antivirus: true, disk_encryption: true,
    location_country: user?.site === "Nairobi" ? "KE" : user?.site === "Rio" ? "BR" : "CH",
    trusted_network: true,
  });

  const [mfaOpen, setMfaOpen] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [pendingResource, setPendingResource] = useState(null);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [lastDecision, setLastDecision] = useState(null);

  const detectPosture = async () => {
    setPostureLoading(true);
    try {
      const hints = await collectClientHints();
      const geo = await requestGeolocation();
      setGeoPermission(geo.permission);
      if (geo.permission) {
        hints.geo_lat = geo.lat;
        hints.geo_lon = geo.lon;
        hints.geo_permission = true;
      } else {
        hints.geo_permission = false;
      }
      const { data } = await api.post("/posture/detect", { client_hints: hints });
      setPosture(data);
      // Register device if not yet done (fire & forget)
      if (!isSimulated && data.device_id) {
        api.post("/devices/register", {
          device_id: hints.device_id,
          fingerprint: hints.fingerprint,
          name: `${hints.platform || navigator.platform} · ${user?.name}`,
        }).catch(() => {});
      }
    } catch (err) {
      toast.error("Erreur détection posture");
    } finally {
      setPostureLoading(false);
    }
  };

  useEffect(() => {
    api.get("/resources").then((r) => setResources(r.data));
    detectPosture();
    // eslint-disable-next-line
  }, [user?.mode]);

  const currentPosture = () => {
    if (isSimulated) return simPosture;
    if (!posture) return null;
    return {
      os: posture.os,
      os_updated: posture.os_updated,
      antivirus: posture.antivirus,
      disk_encryption: posture.disk_encryption,
      location_country: posture.location_country === "??" ? "" : posture.location_country,
      trusted_network: posture.trusted_network,
      is_managed: posture.is_managed,
      geo_permission: posture.geo_permission,
    };
  };

  const attemptAccess = async (resource, code = null) => {
    const p = currentPosture();
    if (!p) { toast.error("Posture non disponible"); return; }
    try {
      const { data } = await api.post(`/resources/${resource._id}/access`, {
        resource_id: resource._id, posture: p, mfa_code: code,
      });
      if (data.requires_mfa) {
        setPendingResource(resource);
        setMfaOpen(true);
        setMfaCode("");
        return;
      }
      setLastDecision(data);
      if (data.decision === "allow" || data.decision === "jit") {
        // Navigate to real data viewer
        sessionStorage.setItem(`zt_session_${resource._id}`, data.session_token || "");
        toast.success(`Accès autorisé — ${data.policy_name || "policy"}`);
        navigate(`/app/resource/${resource._id}`);
      } else {
        // Denied — open decision dialog
        setDecisionOpen(true);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const confirmMfa = async () => {
    if (mfaCode.length < 6 || !pendingResource) return;
    await attemptAccess(pendingResource, mfaCode);
    setMfaOpen(false);
  };

  return (
    <div className="space-y-6" data-testid="catalog-page">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">
          {t("catalog")}
        </div>
        <h1 className="text-3xl font-semibold tracking-tight mt-1">Ressources protégées</h1>
      </div>

      {/* Posture panel */}
      {isSimulated ? (
        <SimulatedPosturePanel value={simPosture} onChange={setSimPosture} t={t} />
      ) : (
        <>
          {geoPermission === false && (
            <Card className="bg-rose-500/10 border-rose-500/40 rounded-sm p-4"
                  data-testid="geo-permission-warning">
              <div className="flex items-start gap-3">
                <Globe2 className="h-5 w-5 text-rose-400 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Autorisation de localisation requise</div>
                  <div className="mt-1 text-xs text-white/70">
                    La politique Zero Trust exige la localisation active du terminal.
                    Autorisez la géolocalisation dans votre navigateur puis rafraîchissez.
                  </div>
                </div>
                <Button size="sm" onClick={detectPosture}
                        data-testid="retry-geo-btn"
                        className="bg-rose-500 text-white hover:bg-rose-400">
                  Réessayer
                </Button>
              </div>
            </Card>
          )}
          <RealPosturePanel posture={posture} loading={postureLoading} onRefresh={detectPosture} />
        </>
      )}

      {/* Resource grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {resources.map((r) => {
          const Icon = ICONS[r.type] || Terminal;
          return (
            <Card key={r._id} className="bg-white/[0.02] border-white/10 rounded-sm p-4 flex flex-col"
                  data-testid={`resource-card-${r._id}`}>
              <div className="flex items-start justify-between">
                <Icon className="h-5 w-5 text-cyan-400" />
                <StatusBadge status={r.sensitivity === "secret" ? "blocked" : r.sensitivity === "confidential" ? "suspicious" : "trusted"}>
                  {r.sensitivity}
                </StatusBadge>
              </div>
              <div className="mt-3 text-sm font-semibold tracking-tight">{r.name}</div>
              <div className="mt-1 text-xs text-white/50 line-clamp-2 flex-1">{r.description}</div>
              <div className="mt-3 flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">
                  {r.type} · {r.site}
                </div>
                <Button
                  size="sm"
                  data-testid={`request-access-${r._id}`}
                  onClick={() => attemptAccess(r)}
                  disabled={!currentPosture()}
                  className="bg-cyan-500 text-black hover:bg-cyan-400 h-7 text-xs"
                >
                  {t("request_access")}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Decision dialog (only shown on deny) */}
      <Dialog open={decisionOpen} onOpenChange={setDecisionOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg"
                       data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span>Accès refusé</span>
              {lastDecision && <StatusBadge status="blocked">{lastDecision.decision}</StatusBadge>}
            </DialogTitle>
          </DialogHeader>
          {lastDecision && (
            <div className="space-y-3 text-sm">
              <div className="border border-white/10 rounded-sm p-3">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">Ressource</div>
                <div className="mt-1 font-semibold">{lastDecision.resource?.name}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-white/10 rounded-sm p-3">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">Politique</div>
                  <div className="mt-1">{lastDecision.policy_name || "default_deny"}</div>
                </div>
                <div className="border border-white/10 rounded-sm p-3">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">{t("risk_score")}</div>
                  <div className="mt-1 font-mono">{lastDecision.risk_score}/100</div>
                </div>
              </div>
              <div className="border border-rose-500/30 bg-rose-500/5 rounded-sm p-3">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-rose-300">Raison</div>
                <div className="mt-1 font-mono text-xs text-white/70">{lastDecision.reason}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* MFA additional dialog */}
      <Dialog open={mfaOpen} onOpenChange={setMfaOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-sm"
                       data-testid="mfa-additional-dialog">
          <DialogHeader><DialogTitle>MFA additionnelle requise</DialogTitle></DialogHeader>
          <p className="text-sm text-white/60">
            La ressource est classée <span className="text-orange-400">secret</span>. Entrez un code TOTP pour continuer.
          </p>
          <InputOTP maxLength={6} value={mfaCode} onChange={setMfaCode}
                    data-testid="mfa-additional-input">
            <InputOTPGroup>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <InputOTPSlot key={i} index={i} className="border-white/10 bg-white/[0.03]" />
              ))}
            </InputOTPGroup>
          </InputOTP>
          <DialogFooter>
            <Button onClick={confirmMfa} disabled={mfaCode.length < 6}
                    data-testid="mfa-additional-confirm"
                    className="bg-cyan-500 text-black hover:bg-cyan-400">{t("verify")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Real posture panel (auto-detected, read-only) ---------- */
function RealPosturePanel({ posture, loading, onRefresh }) {
  return (
    <Card className="bg-white/[0.02] border-white/10 rounded-sm p-4" data-testid="real-posture-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-cyan-400" /> Posture terminal (détectée)
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status="trusted">DÉTECTION AUTOMATIQUE</StatusBadge>
          <Button size="icon" variant="ghost" onClick={onRefresh}
                  data-testid="refresh-posture"
                  className="h-7 w-7 text-white/60 hover:text-cyan-400 hover:bg-white/5">
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {!posture && loading && (
        <div className="text-xs text-white/40 font-mono">Analyse du terminal en cours…</div>
      )}

      {posture && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <PostureItem
            icon={Cpu} label="Système d'exploitation" ok={!!posture.os && posture.os !== "Unknown"}
            value={
              posture.os_version_obfuscated
                ? `${posture.os || "?"} · version masquée`
                : `${posture.os} ${posture.os_version || ""}`.trim()
            }
            testId="pl-os"
            detail={
              posture.os_version_obfuscated
                ? "Navigateur (Safari/Firefox) : version d'OS non exposée par protection anti-fingerprinting. Utilisez Chrome/Edge pour révéler la vraie version."
                : `User-Agent Client Hints ${posture.client_hints_received ? "(UA-CH)" : "(fallback UA)"}`
            }
          />
          <PostureItem
            icon={ShieldAlert} label="OS à jour" ok={posture.os_updated}
            value={
              posture.os_updated
                ? (posture.os_version_obfuscated ? "OUI (version masquée)" : "OUI")
                : "NON — version obsolète"
            }
            testId="pl-updated"
            detail={
              posture.os_version_obfuscated
                ? "Version masquée par le navigateur (protection anti-fingerprinting) — considérée à jour"
                : (posture.os_updated ? "Version au-dessus du seuil minimum" : "Mise à jour requise")
            }
          />
          <PostureItem
            icon={ShieldCheck} label="Antivirus" ok={posture.antivirus}
            value={posture.antivirus ? "ACTIF" : "NON DÉTECTÉ"}
            testId="pl-av" detail={posture.antivirus_detail}
          />
          <PostureItem
            icon={HardDrive} label="Chiffrement disque" ok={posture.disk_encryption}
            value={posture.disk_encryption ? "ACTIVÉ" : "INCONNU"}
            testId="pl-disk" detail={posture.disk_encryption_detail}
          />
          <PostureItem
            icon={Lock} label="Terminal géré par l'organisation" ok={posture.is_managed}
            value={posture.is_managed ? "OUI (enrôlé)" :
                   posture.device_status === "pending" ? "EN ATTENTE d'approbation admin" :
                   posture.device_status === "revoked" ? "RÉVOQUÉ" : "NON ENRÔLÉ"}
            testId="pl-managed"
            detail={posture.device_id ? `Device ID: ${posture.device_id.slice(0, 12)}…` : "Aucun device_id"}
          />
          <PostureItem
            icon={Globe2} label="Localisation" ok={posture.location_country !== "??"}
            value={
              posture.location_country === "??" ? "Inconnue" :
              `${posture.location_country}${posture.location_city ? " · " + posture.location_city : ""}`
            }
            testId="pl-country"
            detail={
              posture.location_source === "browser_gps" ? "GPS navigateur (autorisé)" :
              posture.location_source === "ip" ? `IP: ${posture.ip}${posture.ip_private ? " (privée)" : ""}` :
              "Non détectée"
            }
          />
          <PostureItem
            icon={ShieldCheck} label="Consentement géolocalisation" ok={posture.geo_permission === true}
            value={
              posture.geo_permission === true ? "ACCORDÉ" :
              posture.geo_permission === false ? "REFUSÉ" : "NON DEMANDÉ"
            }
            testId="pl-geo-perm"
            detail={posture.geo_permission ? "navigator.geolocation autorisé" : "Localisation navigateur non disponible"}
          />
          <PostureItem
            icon={Wifi} label="Réseau de confiance" ok={posture.trusted_network}
            value={posture.trusted_network ? "OUI" : "NON — réseau externe"}
            testId="pl-trusted-net"
            detail={posture.ip_private ? "Réseau interne DNDi" :
                    (posture.trusted_network ? "Pays autorisé" : "Pays non listé dans la posture")}
          />
        </div>
      )}
    </Card>
  );
}

function PostureItem({ icon: Icon, label, ok, value, detail, testId }) {
  return (
    <div className={cn(
      "border rounded-sm p-3",
      ok ? "border-emerald-500/20 bg-emerald-500/[0.03]" : "border-rose-500/25 bg-rose-500/[0.03]"
    )} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={cn("h-4 w-4", ok ? "text-emerald-400" : "text-rose-400")} />
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/60">{label}</span>
        </div>
        <StatusBadge status={ok ? "trusted" : "blocked"}>{ok ? "OK" : "KO"}</StatusBadge>
      </div>
      <div className="mt-1.5 text-sm font-semibold">{value}</div>
      {detail && <div className="mt-0.5 text-[10px] font-mono text-white/40 line-clamp-2">{detail}</div>}
    </div>
  );
}

/* ---------- Simulated posture panel (interactive toggles) ---------- */
function SimulatedPosturePanel({ value, onChange, t }) {
  return (
    <Card className="bg-white/[0.02] border-orange-500/25 rounded-sm p-4"
          data-testid="simulated-posture-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-orange-400" /> Posture terminal (Mode Simulé — éditable)
        </div>
        <StatusBadge status="warning">SIMULATION</StatusBadge>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <PostureToggle label={t("os_updated")} v={value.os_updated}
                       onChange={(v) => onChange({ ...value, os_updated: v })} testId="posture-os-updated" />
        <PostureToggle label={t("antivirus")} v={value.antivirus}
                       onChange={(v) => onChange({ ...value, antivirus: v })} testId="posture-antivirus" />
        <PostureToggle label={t("disk_encryption")} v={value.disk_encryption}
                       onChange={(v) => onChange({ ...value, disk_encryption: v })} testId="posture-encryption" />
        <PostureToggle label={t("trusted_network")} v={value.trusted_network}
                       onChange={(v) => onChange({ ...value, trusted_network: v })} testId="posture-trusted-net" />
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">Pays</Label>
          <Input value={value.location_country}
                 onChange={(e) => onChange({ ...value, location_country: e.target.value })}
                 data-testid="posture-country"
                 className="mt-1 bg-white/[0.03] border-white/10 font-mono" />
        </div>
      </div>
    </Card>
  );
}

function PostureToggle({ label, v, onChange, testId }) {
  return (
    <div className="flex items-center justify-between border border-white/10 rounded-sm px-3 py-2">
      <Label className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">{label}</Label>
      <Switch checked={v} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}
